import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudDownload,
  CloudUpload,
  Description,
  FileOpen,
  History,
  Undo,
  Visibility,
} from '@mui/icons-material';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { saveAs } from 'file-saver';

import { useTranslation } from '../../i18n';
import { apiGet } from '../../utils/apiClient';
import AppBarSpacer from '../../components/common/AppBarSpacer';
import { ManagementTableHeaderCell } from '../../components/common/ManagementTable';
import MiniDrawer from '../../components/MiniDrawer';
import PageHeader from '../../components/common/PageHeader';
import { notifyActionSuccess, notifyFailure } from '../../utils/toast';
import {
  ImportLog,
  ImportLogDetail,
  ImportLogRowResult,
  ImportPreview,
  ImportValidation,
  MasterEntity,
  commitImport,
  downloadMasterData,
  downloadMasterDataTemplate,
  getImportLog,
  listImportLogs,
  previewImport,
  revertImport,
} from '../../utils/masterDataApi';

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

type ImportTab = {
  // Tab value + local id (unique). Not necessarily a backend entity.
  key: string;
  // Backend entity key for the generic /master-data API; null = not wired yet.
  entity: MasterEntity | null;
  labelKey: string;
  canImport: boolean;
  canExport: boolean;
  canTemplate: boolean;
  // false → tab is shown but renders a "coming soon" panel (no import/export).
  ready: boolean;
};

// Requested tab order: User, Coach, Trial, Registration, Class, Master Data
// (students), Report — plus the existing Service catalog importer kept as an
// extra tab. Coach/Trial/Class/Master Data/Report are not wired yet (see the
// Import Data feature plan, phases B2+).
const TABS: ImportTab[] = [
  { key: 'user', entity: 'user', labelKey: 'masterData.entityUsers', canImport: true, canExport: true, canTemplate: true, ready: true },
  { key: 'coach', entity: 'coach', labelKey: 'masterData.entityCoach', canImport: true, canExport: true, canTemplate: true, ready: true },
  { key: 'trial', entity: 'trial', labelKey: 'masterData.entityTrial', canImport: true, canExport: true, canTemplate: true, ready: true },
  { key: 'registration', entity: 'registration', labelKey: 'masterData.entityRegistrations', canImport: true, canExport: true, canTemplate: true, ready: true },
  { key: 'class', entity: 'class', labelKey: 'masterData.entityClass', canImport: true, canExport: true, canTemplate: true, ready: true },
  { key: 'master-data', entity: 'master-data', labelKey: 'masterData.entityMasterData', canImport: true, canExport: true, canTemplate: true, ready: true },
  { key: 'report', entity: null, labelKey: 'masterData.entityReport', canImport: false, canExport: true, canTemplate: false, ready: true },
  { key: 'service-catalog', entity: 'service-catalog', labelKey: 'masterData.entityServiceCatalog', canImport: true, canExport: true, canTemplate: true, ready: true },
];

const statusColor: Record<
  ImportLog['status'],
  'success' | 'default' | 'error'
> = {
  committed: 'success',
  reverted: 'default',
  failed: 'error',
};

const ImportDataSetting = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  // Active tab key (defaults to the first ready tab).
  const [activeKey, setActiveKey] = useState<string>('user');
  const activeTab = TABS.find((tab) => tab.key === activeKey) ?? TABS[0];
  const entity = activeTab.entity;
  // History key: entity-backed tabs use their entity; the Report tab logs its
  // downloads under the 'report' key so it gets a history too.
  const logKey = activeTab.entity ?? (activeTab.key === 'report' ? 'report' : null);

  const [downloading, setDownloading] = useState<'catalog' | 'template' | null>(
    null,
  );

  // Report tab (export-only) state
  const [reportLoading, setReportLoading] = useState<string | null>(null);
  const [revenueMonth, setRevenueMonth] = useState('');

  // Upload / preview state
  const [file, setFile] = useState<File | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [validation, setValidation] = useState<ImportValidation | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // History state
  const [logs, setLogs] = useState<ImportLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(0);
  const [logsPerPage, setLogsPerPage] = useState(10);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [detail, setDetail] = useState<ImportLogDetail | null>(null);
  const [rowDetail, setRowDetail] = useState<ImportLogRowResult | null>(null);
  const [revertTarget, setRevertTarget] = useState<ImportLog | null>(null);
  const [reverting, setReverting] = useState(false);

  const resetUpload = useCallback(() => {
    setFile(null);
    setPreview(null);
    setValidation(null);
    setSuccessMessage('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const loadLogs = useCallback(async () => {
    // Tabs with no history key have nothing to load.
    if (!logKey) {
      setLogs([]);
      setLogsTotal(0);
      return;
    }
    setLoadingLogs(true);
    try {
      const res = await listImportLogs({
        entity: logKey,
        page: logsPage + 1,
        limit: logsPerPage,
      });
      setLogs(res.data);
      setLogsTotal(res.metadata.total);
    } catch {
      notifyFailure();
    } finally {
      setLoadingLogs(false);
    }
  }, [logKey, logsPage, logsPerPage]);

  // Reset the upload form and go back to page 1 when switching entity.
  useEffect(() => {
    resetUpload();
    setLogsPage(0);
  }, [entity, resetUpload]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const handleTabChange = (_e: React.SyntheticEvent, value: string) =>
    setActiveKey(value);

  const handleDownload = async (kind: 'catalog' | 'template') => {
    if (!entity) return;
    setDownloading(kind);
    try {
      if (kind === 'catalog') await downloadMasterData(entity);
      else await downloadMasterDataTemplate(entity);
      notifyActionSuccess(t('masterData.downloadStarted'));
      void loadLogs();
    } catch {
      notifyFailure();
    } finally {
      setDownloading(null);
    }
  };

  // Report tab: stream an xlsx from a /report/export-* endpoint (export only).
  const downloadReport = async (key: string, url: string, filename: string) => {
    setReportLoading(key);
    try {
      const res = await apiGet<Blob>(url, { responseType: 'blob' });
      saveAs(new Blob([res as unknown as BlobPart], { type: XLSX_MIME }), filename);
      notifyActionSuccess(t('masterData.downloadStarted'));
    } catch {
      notifyFailure();
    } finally {
      setReportLoading(null);
    }
  };

  // Accept a file from the picker or a drag-and-drop. Dropped files sometimes
  // arrive with an empty MIME type, so also accept by the .xlsx extension.
  const selectFile = (selected: File | null) => {
    setPreview(null);
    setValidation(null);
    setSuccessMessage('');
    if (selected) {
      const isXlsx =
        selected.type === XLSX_MIME ||
        selected.name.toLowerCase().endsWith('.xlsx');
      if (!isXlsx) {
        setFile(null);
        notifyFailure();
        return;
      }
    }
    setFile(selected);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0] || null);
  };

  const [dragActive, setDragActive] = useState(false);
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    selectFile(event.dataTransfer.files?.[0] || null);
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!dragActive) setDragActive(true);
  };
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handlePreview = async () => {
    if (!file || !entity) return;
    setPreviewing(true);
    setPreview(null);
    setValidation(null);
    setSuccessMessage('');
    try {
      const outcome = await previewImport(entity, file);
      if (outcome.ok) setPreview(outcome.preview);
      else setValidation(outcome.validation);
    } catch {
      notifyFailure();
    } finally {
      setPreviewing(false);
    }
  };

  const handleConfirm = async () => {
    if (!file || !entity) return;
    setCommitting(true);
    try {
      const outcome = await commitImport(entity, file);
      if (outcome.ok) {
        setSuccessMessage(
          t('masterData.importSuccess', {
            total: outcome.summary.totalRows,
            created: outcome.summary.created,
            updated: outcome.summary.updated,
          }),
        );
        notifyActionSuccess(t('masterData.importSuccessToast'));
        setFile(null);
        setPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        void loadLogs();
      } else {
        setPreview(null);
        setValidation(outcome.validation);
      }
    } catch {
      notifyFailure();
    } finally {
      setCommitting(false);
    }
  };

  const openDetail = async (log: ImportLog) => {
    try {
      const res = await getImportLog(log.id);
      setDetail(res.data);
    } catch {
      notifyFailure();
    }
  };

  const handleRevert = async () => {
    if (!revertTarget) return;
    setReverting(true);
    try {
      const res = await revertImport(revertTarget.id);
      notifyActionSuccess(
        t('masterData.revertSuccess', {
          count: res.data.revertedMutations,
        }),
      );
      setRevertTarget(null);
      void loadLogs();
    } catch {
      notifyFailure();
    } finally {
      setReverting(false);
    }
  };

  const actionLabel = (action: ImportLog['action']) =>
    action === 'import'
      ? t('masterData.actionImport')
      : action === 'export'
        ? t('masterData.actionExport')
        : t('masterData.actionTemplate');

  return (
    <div className="flex h-screen">
      <MiniDrawer open={open} setOpen={setOpen} />
      <main className="flex-1 overflow-auto app-page-bg">
        <AppBarSpacer />
        <div className="h-full px-6 py-4">
          <PageHeader
            title={t('masterData.title')}
            subtitle={t('masterData.subtitle')}
          />

          <Tabs
            value={activeKey}
            onChange={handleTabChange}
            sx={{ mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={t(tab.labelKey)} />
            ))}
          </Tabs>

          <Stack spacing={3}>
            {/* Not-yet-wired tabs */}
            {!activeTab.ready && (
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="body1" color="text.secondary">
                  {t('masterData.comingSoon')}
                </Typography>
              </Card>
            )}

            {/* Report tab — export only, uses the /report/export-* endpoints */}
            {activeTab.ready && activeTab.key === 'report' && (
              <Card variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700} color="secondary.main">
                  {t('masterData.reportHeading')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t('masterData.reportDescription')}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Button
                    variant="contained"
                    startIcon={
                      reportLoading === 'registration' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudDownload />
                      )
                    }
                    disabled={reportLoading !== null}
                    onClick={() =>
                      void downloadReport(
                        'registration',
                        '/report/export-registration-report',
                        'registration_report.xlsx',
                      )
                    }
                  >
                    {t('masterData.reportRegistration')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      reportLoading === 'trial' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudDownload />
                      )
                    }
                    disabled={reportLoading !== null}
                    onClick={() =>
                      void downloadReport(
                        'trial',
                        '/report/export-trial-report',
                        'trial_report.xlsx',
                      )
                    }
                  >
                    {t('masterData.reportTrial')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      reportLoading === 'customer' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudDownload />
                      )
                    }
                    disabled={reportLoading !== null}
                    onClick={() =>
                      void downloadReport(
                        'customer',
                        '/report/export-customer-report',
                        'customer_report.xlsx',
                      )
                    }
                  >
                    {t('masterData.reportCustomer')}
                  </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    type="month"
                    size="small"
                    label={t('masterData.reportRevenueMonth')}
                    InputLabelProps={{ shrink: true }}
                    value={revenueMonth}
                    onChange={(event) => setRevenueMonth(event.target.value)}
                    sx={{ width: 220 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={
                      reportLoading === 'revenue' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudDownload />
                      )
                    }
                    disabled={reportLoading !== null || !revenueMonth}
                    onClick={() => {
                      const [year, month] = revenueMonth.split('-');
                      void downloadReport(
                        'revenue',
                        `/report/export-revenue-report?month=${Number(
                          month,
                        )}&year=${Number(year)}`,
                        `revenue_report_${revenueMonth}.xlsx`,
                      );
                    }}
                  >
                    {t('masterData.reportRevenue')}
                  </Button>
                </Stack>
              </Card>
            )}

            {/* Download */}
            {activeTab.ready &&
              activeTab.entity &&
              (activeTab.canExport || activeTab.canTemplate) && (
                <Card variant="outlined" sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="secondary.main"
                  >
                    {t('masterData.downloadHeading')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {t('masterData.downloadDescription')}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    {activeTab.canExport && (
                      <Button
                        variant="contained"
                        startIcon={
                          downloading === 'catalog' ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <CloudDownload />
                          )
                        }
                        disabled={downloading !== null}
                        onClick={() => void handleDownload('catalog')}
                      >
                        {t('masterData.downloadData')}
                      </Button>
                    )}
                    {activeTab.canTemplate && (
                      <Button
                        variant="outlined"
                        startIcon={
                          downloading === 'template' ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <Description />
                          )
                        }
                        disabled={downloading !== null}
                        onClick={() => void handleDownload('template')}
                      >
                        {t('masterData.downloadTemplate')}
                      </Button>
                    )}
                  </Stack>
                </Card>
              )}

            {/* Upload + preview */}
            {activeTab.ready && activeTab.entity && activeTab.canImport && (
            <Card variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} color="secondary.main">
                {t('masterData.uploadHeading')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('masterData.uploadDescription')}
              </Typography>

              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: '2px dashed',
                  borderColor: dragActive ? 'primary.main' : 'divider',
                  backgroundColor: dragActive
                    ? 'action.hover'
                    : 'transparent',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<FileOpen />}
                  >
                    {t('masterData.chooseFile')}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {file ? file.name : t('masterData.dropHint')}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant="outlined"
                    startIcon={
                      previewing ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <Visibility />
                      )
                    }
                    disabled={!file || previewing || committing}
                    onClick={() => void handlePreview()}
                  >
                    {t('masterData.previewButton')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      committing ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudUpload />
                      )
                    }
                    disabled={!file || !preview || committing}
                    onClick={() => void handleConfirm()}
                  >
                    {t('masterData.confirmImport')}
                  </Button>
                </Stack>
              </Box>

              {successMessage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {successMessage}
                </Alert>
              )}

              {preview && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {t('masterData.previewReady', {
                      create: preview.willCreate,
                      update: preview.willUpdate,
                    })}
                  </Alert>
                  <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <ManagementTableHeaderCell>
                            {t('masterData.colRow')}
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            {t('masterData.colAction')}
                          </ManagementTableHeaderCell>
                          <ManagementTableHeaderCell>
                            {t('masterData.colSummary')}
                          </ManagementTableHeaderCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {preview.plan.map((row) => (
                          <TableRow key={row.row}>
                            <TableCell>{row.row}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                color={
                                  row.action === 'create'
                                    ? 'primary'
                                    : 'warning'
                                }
                                label={
                                  row.action === 'create'
                                    ? t('masterData.actionCreate')
                                    : t('masterData.actionUpdate')
                                }
                              />
                            </TableCell>
                            <TableCell>{row.summary}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              )}

              {validation && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {t('masterData.validationFailed', {
                      count: validation.errorCount,
                    })}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <List dense disablePadding>
                    {validation.errors.slice(0, 50).map((rowError) => (
                      <ListItem
                        key={rowError.row}
                        disableGutters
                        sx={{ py: 0 }}
                      >
                        <ListItemText
                          primary={t('masterData.rowError', {
                            row: rowError.row,
                            messages: rowError.messages.join('; '),
                          })}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {validation.errors.length > 50 && (
                    <Typography variant="caption" color="text.secondary">
                      {t('masterData.andMore', {
                        count: validation.errors.length - 50,
                      })}
                    </Typography>
                  )}
                </Alert>
              )}
            </Card>
            )}

            {/* History */}
            {activeTab.ready && logKey && (
            <Card variant="outlined" sx={{ p: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <History fontSize="small" color="secondary" />
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="secondary.main"
                >
                  {t('masterData.historyHeading')}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('masterData.historyDescription')}
              </Typography>

              {loadingLogs ? (
                <CircularProgress size={22} />
              ) : logs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('masterData.noHistory')}
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <ManagementTableHeaderCell>
                          {t('masterData.colWhen')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colAction')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colFile')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colStatus')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colRows')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colBy')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell align="right">
                          {t('masterData.colActions')}
                        </ManagementTableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            {new Date(log.createdAt).toLocaleString()}
                          </TableCell>
                          <TableCell>{actionLabel(log.action)}</TableCell>
                          <TableCell>{log.fileName || '—'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={statusColor[log.status]}
                              label={t(`masterData.status_${log.status}`)}
                            />
                          </TableCell>
                          <TableCell>
                            {log.action === 'import'
                              ? `${log.createdRows}+ / ${log.updatedRows}~${
                                  log.errorRows ? ` / ${log.errorRows}✕` : ''
                                }`
                              : log.totalRows}
                          </TableCell>
                          <TableCell>{log.performedBy?.name || '—'}</TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                            >
                              <Button
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => void openDetail(log)}
                              >
                                {t('masterData.viewDetail')}
                              </Button>
                              {log.action === 'import' &&
                                log.status === 'committed' && (
                                  <Button
                                    size="small"
                                    color="warning"
                                    startIcon={<Undo />}
                                    onClick={() => setRevertTarget(log)}
                                  >
                                    {t('masterData.revert')}
                                  </Button>
                                )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {logsTotal > 0 && (
                <TablePagination
                  component="div"
                  count={logsTotal}
                  page={logsPage}
                  onPageChange={(_event, newPage) => setLogsPage(newPage)}
                  rowsPerPage={logsPerPage}
                  onRowsPerPageChange={(event) => {
                    setLogsPerPage(parseInt(event.target.value, 10));
                    setLogsPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              )}
            </Card>
            )}
          </Stack>
        </div>
      </main>

      {/* Detail dialog */}
      <Dialog
        open={detail !== null}
        onClose={() => setDetail(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('masterData.detailTitle', { id: detail?.id ?? '' })}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {actionLabel(detail.action)} · {detail.fileName || '—'} ·{' '}
                {t(`masterData.status_${detail.status}`)}
              </Typography>
              {detail.rowResults && detail.rowResults.length > 0 ? (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <ManagementTableHeaderCell>
                          {t('masterData.colRow')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colStatus')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colSummary')}
                        </ManagementTableHeaderCell>
                        <ManagementTableHeaderCell>
                          {t('masterData.colActions')}
                        </ManagementTableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.rowResults.map((r) => (
                        <TableRow key={r.row}>
                          <TableCell>{r.row}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={
                                r.status === 'success' ? 'success' : 'error'
                              }
                              label={
                                r.status === 'success'
                                  ? r.action === 'update'
                                    ? t('masterData.actionUpdate')
                                    : t('masterData.actionCreate')
                                  : t('masterData.colStatus')
                              }
                            />
                          </TableCell>
                          <TableCell>
                            {r.status === 'success'
                              ? `#${r.recordId ?? ''}`
                              : (r.messages || []).join('; ')}
                          </TableCell>
                          <TableCell>
                            <Tooltip title={t('masterData.viewRow')}>
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={t('masterData.viewRow')}
                                onClick={() => setRowDetail(r)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('masterData.noRowDetail')}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>
            {t('masterData.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single-row detail dialog */}
      <Dialog
        open={rowDetail !== null}
        onClose={() => setRowDetail(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('masterData.rowDetailTitle', { row: rowDetail?.row ?? '' })}
        </DialogTitle>
        <DialogContent dividers>
          {rowDetail && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('masterData.colRow')}
                </Typography>
                <Typography variant="body2">{rowDetail.row}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('masterData.colStatus')}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    color={rowDetail.status === 'success' ? 'success' : 'error'}
                    label={t(`masterData.status_${rowDetail.status}`)}
                  />
                </Box>
              </Box>
              {rowDetail.status === 'success' ? (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('masterData.colAction')}
                    </Typography>
                    <Typography variant="body2">
                      {rowDetail.action === 'update'
                        ? t('masterData.actionUpdate')
                        : t('masterData.actionCreate')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('masterData.recordId')}
                    </Typography>
                    <Typography variant="body2">
                      #{rowDetail.recordId ?? '—'}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('masterData.messages')}
                  </Typography>
                  {(rowDetail.messages || []).length > 0 ? (
                    <List dense disablePadding>
                      {(rowDetail.messages || []).map((message, index) => (
                        <ListItem key={index} disableGutters sx={{ py: 0 }}>
                          <ListItemText
                            primary={message}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2">—</Typography>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRowDetail(null)}>
            {t('masterData.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert confirm dialog */}
      <Dialog
        open={revertTarget !== null}
        onClose={() => setRevertTarget(null)}
      >
        <DialogTitle>{t('masterData.revertConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('masterData.revertConfirmBody', {
              file: revertTarget?.fileName || `#${revertTarget?.id ?? ''}`,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertTarget(null)} disabled={reverting}>
            {t('masterData.cancel')}
          </Button>
          <Button
            color="warning"
            variant="contained"
            startIcon={
              reverting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Undo />
              )
            }
            disabled={reverting}
            onClick={() => void handleRevert()}
          >
            {t('masterData.revert')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ImportDataSetting;
